/**
 * Утилиты для работы с файлами
 */
import React from 'react'
import { 
  FileText, 
  Image, 
  Video, 
  Music, 
  Archive, 
  BarChart3, 
  Wrench, 
  Settings, 
  Box,
  Film,
  File
} from 'lucide-react'

export const getFileIcon = (fileType?: string, fileName?: string, size?: number) => {
  const iconSize = size || 20
  const type = fileType?.toLowerCase() || fileName?.split('.').pop()?.toLowerCase() || ''
  
  const iconProps = { size: iconSize, className: "text-gray-600" }
  
  // Документы
  if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(type)) {
    return <FileText {...iconProps} className="text-red-500" />
  }
  
  // Изображения
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'webp', 'svg'].includes(type)) {
    return <Image {...iconProps} className="text-blue-500" />
  }
  
  // CAD файлы (AutoCAD)
  if (['dwg', 'dxf'].includes(type)) {
    return <Settings {...iconProps} className="text-orange-500" />
  }
  
  // CAD файлы (STEP/IGES и другие)
  if (['step', 'stp', 'iges', 'igs', 'prt', 'md3', 'sldprt', 'sldasm', 'slddrw', 'x_t', 'x_b'].includes(type)) {
    return <Wrench {...iconProps} className="text-green-500" />
  }
  
  // Архивы
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(type)) {
    return <Archive {...iconProps} className="text-purple-500" />
  }
  
  // Таблицы
  if (['xls', 'xlsx', 'csv', 'ods'].includes(type)) {
    return <BarChart3 {...iconProps} className="text-green-600" />
  }
  
  // Видео
  if (['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'].includes(type)) {
    return <Video {...iconProps} className="text-indigo-500" />
  }
  
  // Аудио
  if (['mp3', 'wav', 'flac', 'ogg', 'aac'].includes(type)) {
    return <Music {...iconProps} className="text-pink-500" />
  }
  
  // По умолчанию
  return <File {...iconProps} />
}

export const canPreviewFile = (fileType?: string, fileName?: string) => {
  // Сначала пробуем определить тип по расширению файла (более точно)
  const extensionType = fileName?.split('.').pop()?.toLowerCase() || ''
  
  // Если расширение не определено, используем fileType
  const type = extensionType || fileType?.toLowerCase() || ''
  
  // Изображения - можно просматривать в lightbox или браузере
  const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'webp', 'svg']
  
  // PDF - можно просматривать во встроенном просмотрщике браузера
  const pdfTypes = ['pdf']
  
  // Текстовые файлы - можно просматривать в браузере
  const textTypes = ['txt', 'csv']
  
  return imageTypes.includes(type) || pdfTypes.includes(type) || textTypes.includes(type)
}

export const isImageFile = (fileType?: string, fileName?: string) => {
  const extensionType = fileName?.split('.').pop()?.toLowerCase() || ''
  const type = extensionType || fileType?.toLowerCase() || ''
  const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'webp', 'svg']
  return imageTypes.includes(type)
}

// Алиас для обратной совместимости
export const isImageFileLike = isImageFile

export const isPdfFile = (fileType?: string, fileName?: string) => {
  const extensionType = fileName?.split('.').pop()?.toLowerCase() || ''
  const type = extensionType || fileType?.toLowerCase() || ''
  return type === 'pdf'
}

export const isTextFile = (fileType?: string, fileName?: string) => {
  const extensionType = fileName?.split('.').pop()?.toLowerCase() || ''
  const type = extensionType || fileType?.toLowerCase() || ''
  const textTypes = ['txt', 'csv']
  return textTypes.includes(type)
}

export const formatFileSize = (bytes?: number) => {
  if (!bytes) return ''
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

export const getFileTypeCategory = (fileType?: string, fileName?: string) => {
  const type = fileType?.toLowerCase() || fileName?.split('.').pop()?.toLowerCase() || ''
  
  const categories = {
    image: ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'tiff', 'tif', 'webp', 'svg'],
    document: ['pdf', 'doc', 'docx', 'txt', 'rtf'],
    cad: ['dwg', 'dxf', 'step', 'stp', 'iges', 'igs', 'prt', 'md3', 'sldprt', 'sldasm', 'slddrw', 'x_t', 'x_b'],
    archive: ['zip', 'rar', '7z', 'tar', 'gz'],
    spreadsheet: ['xls', 'xlsx', 'csv', 'ods'],
    video: ['mp4', 'avi', 'mov', 'wmv', 'mkv', 'webm'],
    audio: ['mp3', 'wav', 'flac', 'ogg', 'aac']
  }
  
  for (const [category, types] of Object.entries(categories)) {
    if (types.includes(type)) {
      return category
    }
  }
  
  return 'other'
}

export const getFileTypeDescription = (fileType?: string, fileName?: string) => {
  const type = fileType?.toLowerCase() || fileName?.split('.').pop()?.toLowerCase() || ''
  
  const descriptions: Record<string, string> = {
    // Документы
    'pdf': 'PDF документ',
    'doc': 'Документ Word',
    'docx': 'Документ Word',
    'txt': 'Текстовый файл',
    'rtf': 'RTF документ',
    
    // Изображения
    'jpg': 'Изображение JPEG',
    'jpeg': 'Изображение JPEG',
    'png': 'Изображение PNG',
    'gif': 'Анимированное изображение',
    'bmp': 'Изображение BMP',
    'tiff': 'Изображение TIFF',
    'tif': 'Изображение TIFF',
    'webp': 'Изображение WebP',
    'svg': 'Векторное изображение',
    
    // CAD файлы
    'dwg': 'Чертеж AutoCAD',
    'dxf': 'Чертеж DXF',
    'step': '3D модель STEP',
    'stp': '3D модель STEP',
    'iges': '3D модель IGES',
    'igs': '3D модель IGES',
    'prt': '3D деталь',
    'md3': '3D модель',
    
    // SolidWorks
    'sldprt': 'Деталь SolidWorks',
    'sldasm': 'Сборка SolidWorks',
    'slddrw': 'Чертеж SolidWorks',
    
    // Parasolid
    'x_t': '3D модель Parasolid',
    'x_b': '3D модель Parasolid',
    
    // Архивы
    'zip': 'ZIP архив',
    'rar': 'RAR архив',
    '7z': '7-Zip архив',
    'tar': 'TAR архив',
    'gz': 'GZIP архив',
    
    // Таблицы
    'xls': 'Таблица Excel',
    'xlsx': 'Таблица Excel',
    'csv': 'CSV таблица',
    'ods': 'Таблица OpenDocument',
    
    // Видео
    'mp4': 'Видео MP4',
    'avi': 'Видео AVI',
    'mov': 'Видео QuickTime',
    'wmv': 'Видео WMV',
    'mkv': 'Видео MKV',
    'webm': 'Видео WebM',
    
    // Аудио
    'mp3': 'Аудио MP3',
    'wav': 'Аудио WAV',
    'flac': 'Аудио FLAC',
    'ogg': 'Аудио OGG',
    'aac': 'Аудио AAC'
  }
  
  return descriptions[type] || 'Файл'
}